import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createStatusResolver, statusDefinitionSeed, type StatusDefinitionRow, type StatusResolver } from '@bms2/shared';
import { useApi } from '../pages/records/api';

/**
 * 🔒 The one status resolver every pill in the frame reads (decisions 0098,
 * 0639): the seeded `StatusDefinition` rows, fetched once by the shell from the
 * route that serves them, and handed to the list viewer, the tile, the approvals
 * queue and the logins page through this context.
 *
 * 🔨 No screen types a status-to-colour map of its own. A label or a colour
 * group is a row, so an office that renames one changes a row and no code.
 *
 * 💰 Until the rows arrive, and outside the frame, the resolver reads the same
 * rows as the package spells them, which the database suite holds the table to.
 * A pill is therefore drawn in the first paint rather than blank, and a list on
 * a failed read still draws its pills rather than bare words.
 */
export const STATUS_DEFINITIONS_PATH = '/status-definitions';

const SEEDED: StatusResolver = createStatusResolver(statusDefinitionSeed);

const StatusResolverContext = createContext<StatusResolver>(SEEDED);

export function StatusResolverProvider({ resolve, children }: { resolve: StatusResolver; children: ReactNode }) {
  return <StatusResolverContext.Provider value={resolve}>{children}</StatusResolverContext.Provider>;
}

export function useStatusResolver(): StatusResolver {
  return useContext(StatusResolverContext);
}

/** The shell's own read, kept beside the provider so nothing else has to know the path. */
export function useStatusResolverRead(apiBaseUrl: string): StatusResolver {
  const read = useApi<{ rows: StatusDefinitionRow[] }>(STATUS_DEFINITIONS_PATH, apiBaseUrl, { cache: true });
  const rows = read.state.kind === 'ready' ? read.state.data.rows : null;
  return useMemo(() => (rows && rows.length > 0 ? createStatusResolver(rows) : SEEDED), [rows]);
}
